import {
  Globe,
  Building2,
  Users,
  Briefcase,
  Bell,
  ShieldAlert,
  Activity,
  Factory,
  LayoutDashboard,
  Settings,
  Bot,
  Radar,
  type LucideIcon,
} from 'lucide-react';

export interface ModuleChild {
  id: string;
  label: string;
  to: string;
  /** When set, the link is matched as the active tab via exact pathname match (default false: startsWith) */
  exact?: boolean;
  /** Hide from sidebar (route only). */
  hidden?: boolean;
  /** 这一项只给内部 admin 看（父组不是 adminOnly 时用，如「配置中心」里的客户租户）。 */
  adminOnly?: boolean;
  /**
   * 三期新增、默认不进侧边栏。
   *
   * 三期这批要跟工厂端一起上线（工厂端走 portal.link4a.com 独立域名），
   * 但 admin 跟 portal 是同一份静态产物 —— 部署 portal 就必然把这些入口也推上去。
   * 业务员今天打开的界面不该因为一次「给工厂端上线」的部署就多出五个东西。
   *
   * 所以默认藏起来，路由仍在（可深链、可自测）。要放开时把 env
   * `VITE_GENBOX_PHASE3_NAV=1` 打开重新 build，不用改代码。
   */
  phase3?: boolean;
}

export interface ModuleEntry {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Top-level route (used if no children). */
  to?: string;
  /** If true, the module is only rendered for admin users. */
  adminOnly?: boolean;
  /** Sub-routes under this module. */
  children?: ModuleChild[];
}

/**
 * 重组原则：
 *  - 业务员日常 100% 在飞书；admin = 管理 / 着陆 case / 审计 / 配置
 *  - 业务入口放前面，低频排障工具统一收进「运维」
 *  - "建站"独立保留（项目级管理）
 */
/**
 * 三期新增的导航入口要不要显示。
 *
 * 默认关：部署工厂端不该改变业务员今天看到的侧边栏。
 * 统一发布窗口到了，把 `VITE_GENBOX_PHASE3_NAV=1` 打开重新 build 即可。
 */
export function phase3NavEnabled(
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<
    string,
    string | undefined
  >,
): boolean {
  return env['VITE_GENBOX_PHASE3_NAV'] === '1';
}

export const modules: ModuleEntry[] = [
  {
    id: 'overview',
    label: '运营看板',
    icon: LayoutDashboard,
    to: '/crm/dashboard',
  },
  {
    id: 'sites',
    label: '站点',
    icon: Globe,
    children: [
      { id: 'sites-index', label: '站点列表', to: '/', exact: true },
      { id: 'sites-new', label: '新建站点', to: '/sites/new' },
    ],
  },
  {
    id: 'cases',
    label: '客户与询盘',
    icon: Users,
    children: [
      { id: 'crm-leads', label: '客户 / 询盘', to: '/crm/leads' },
      // 报价单**不挂三期开关**：它是现在就在用的东西，而那个开关管的是
      // 「统一发布窗口到了再露出来」的那批（订单、租户体系…）。挂上去等于
      // 做完了业务员也看不见 —— 2026-09-17 就这么白做了一轮。
      { id: 'crm-quotes', label: '报价单', to: '/crm/quotes' },
      { id: 'crm-drafts', label: 'AI 草稿审计', to: '/crm/drafts' },
      { id: 'crm-push-log', label: '业务员推送审计', to: '/crm/push-log' },
      { id: 'crm-escalation', label: 'SLA 升级监控', to: '/crm/escalation' },
    ],
  },
  {
    id: 'catalog',
    label: '供应链',
    icon: Factory,
    children: [
      { id: 'crm-orders', label: '订单', to: '/crm/orders', phase3: true },
      { id: 'crm-suppliers', label: '供应商', to: '/crm/suppliers' },
      // 体检是供应商页里的一个子页签,不单独占菜单位;hidden 只是不渲染,
      // 路由与「供应链」高亮判定都还在(在体检页时这一组仍然是选中态)。
      { id: 'crm-supplier-health', label: '供应商体检', to: '/crm/supplier-health', hidden: true },
      // 工厂绑定同理:供应商页的子页签,不单独占菜单位
      { id: 'crm-binding', label: '工厂绑定', to: '/crm/binding', hidden: true },
      { id: 'crm-products', label: '产品', to: '/crm/products' },
      { id: 'crm-supplier-import', label: '供应商导入 Agent', to: '/crm/migration' },
      // 导入时 mapper 认不出来的列落这儿。挨着导入 Agent 放，是因为它就是那条链路的产物：
      // 导完一批过来看一眼「这次又多出什么列」，比事后发现站上少了一行规格强。
      { id: 'crm-field-candidates', label: '没认出来的列', to: '/crm/field-candidates' },
      { id: 'crm-sourcing', label: '阿里寻源', to: '/crm/sourcing' },
    ],
  },
  {
    /**
     * 工厂客户 —— 付费工厂从签约到跑起来的那条线，以及线上的每一家。
     *
     * 单独成组的理由：这几页本来散在三个地方（租户在配置中心、绑定在供应商页、
     * 出口 IP 和托管状态在运维），谁也看不出它们是一件事。业务上它就是一条 OA：
     * 签约付费 → 工厂扫微信填资料 → 我们认领确认、定开通与到期 →
     * 分配出口 IP → 工厂扫 WhatsApp → 跑起来。「开通流程」那一页就是这条线本身，
     * 其余几页是它的明细。
     *
     * 整组 adminOnly：开通与停用客户是内部的事，一线业务员不该看见 ——
     * 这也是它**不再挂三期开关**的原因，那个开关防的是「业务员今天打开的
     * 界面别多出五个东西」，对 adminOnly 的东西本来就不成立。
     */
    id: 'factory',
    label: '工厂客户',
    icon: Building2,
    adminOnly: true,
    children: [
      { id: 'crm-onboarding', label: '开通流程', to: '/crm/onboarding' },
      { id: 'crm-tenants', label: '工厂名单', to: '/crm/tenants', exact: true },
      { id: 'crm-tenant-detail', label: '工厂详情', to: '/crm/tenants/', hidden: true },
      // G5：哪个档位有哪些功能。脱敏那条不在这里，它是硬规则
      { id: 'crm-tier-capabilities', label: '服务档位', to: '/crm/tier-capabilities' },
      // P5：自动分流拿不准的（端点归属与站点归属打架）落这里等人工定
      { id: 'crm-tenant-assignment', label: '待指派归属', to: '/crm/tenant-assignment' },
      // 工厂有没有真收到通知。通道挂了不会有人主动报给我们，只能自己盯
      { id: 'crm-tenant-notify', label: '通知送达', to: '/crm/tenant-notify' },
    ],
  },
  {
    id: 'config',
    label: '配置中心',
    icon: Settings,
    children: [
      // 业务员管理(/crm/salespeople) 暂从导航撤下：prod 0 条、无写入路径（auto-onboard 未启用，
      // 群审「通过」只查不建），是空壳。路由仍在（可深链），补好飞书通讯录同步/编辑入口后再挂回。
      // 工厂客户那一摊（开通/档位/归属/通知）整组挪去「工厂客户」—— 它们是一条流程，
      // 散在配置中心里等于把流程拆成零件摆着。
      // D1：订单要走哪几个状态由这里配，不同行业一套
      { id: 'crm-order-flows', label: '订单流程', to: '/crm/order-flows', phase3: true },
      { id: 'crm-push-rules', label: '推送规则', to: '/crm/push-rules' },
      { id: 'crm-blocks', label: '黑名单', to: '/crm/blocks' },
      { id: 'crm-inquiry-mailboxes', label: '询盘邮箱', to: '/crm/inquiry-mailboxes' },
      { id: 'crm-inbound-endpoints', label: '来源号码', to: '/crm/inbound-endpoints' },
    ],
  },
  {
    // 运维 —— 工程/排障视角的页面统一收在这里(adminOnly,一线看不到)。
    //
    // 归这儿的判据是「业务员做本职工作时永远不需要打开它」:
    //  · 渠道状态 / LLM 网关:服务是否配好、模型花了多少钱 —— 运维排障
    //  · 模型决策日志:LLM 接受率与时延的 prompt 评测(业务复核在飞书卡片做)
    //  · 客户合并审计:实体识别误并号的数据质量回溯
    //  · 黑名单审计:拉黑/解封留痕(业务操作在「黑名单」页,这里只是留痕)
    //  · 撮合测试:看 Agent 过程的调试页,不是业务员选货的地方
    //  · Agent 任务:后台任务队列
    id: 'ops',
    label: '运维',
    icon: Activity,
    adminOnly: true,
    children: [
      { id: 'crm-templates', label: '话术库', to: '/crm/templates' },
      { id: 'crm-outreach', label: '外发触发', to: '/crm/outreach' },
      { id: 'crm-outreach-history', label: '外发历史', to: '/crm/outreach/history' },
      { id: 'crm-gateway', label: 'LLM 网关', to: '/crm/gateway' },
      { id: 'crm-outbound-channels', label: '出站渠道', to: '/crm/outbound/channels' },
      { id: 'crm-inbound-channels', label: '入站渠道', to: '/crm/inbound/channels' },
      { id: 'agent-tasks', label: 'Agent 任务', to: '/agent/tasks' },
      { id: 'crm-match-playground', label: '撮合测试（Agent 过程）', to: '/crm/match-playground' },
      { id: 'crm-decisions', label: '模型决策日志', to: '/crm/decisions' },
      { id: 'crm-er-audit', label: '客户合并审计', to: '/crm/er-audit' },
      { id: 'crm-blocks-audit', label: '黑名单审计', to: '/crm/blocks/audit' },
      // WhatsApp 通道 —— 一条菜单，进去三个页签（我们的号 / 工厂托管 / 出口 IP 池）。
      // 此前是三条并排的菜单，名字里带着「我们的」「工厂的」，看的人得先猜它们
      // 什么关系。它们是同一条通道的三面，拆成三条菜单只是把内部结构摊给了用户。
      // 三个页签都在 /crm/whatsapp 底下，所以这一条按前缀匹配就能一直高亮着。
      // 旧的 /crm/wa-relay 和 /crm/wa-egress 保留成跳转 —— 文档和历史消息里有这两条链接。
      { id: 'crm-wa', label: 'WhatsApp 通道', to: '/crm/whatsapp' },
    ],
  },
  {
    // H6 竞对情报闭环的四段：监测 → 选题 → 出文 → 收录检查。链路本身跑在后台，
    // 这几页是它唯一的可视化入口（此前只在 Mongo 和飞书群里留痕）。
    // 第四页 2026-08-12 补：前三段只保证「文章上站」，不保证「Google 收了」。
    // 只看文章 —— 产品页/静态页跟着 sitemap 走，不占这里的注意力。
    id: 'seo',
    label: 'SEO 内容',
    icon: Radar,
    children: [
      { id: 'seo-competitors', label: '竞对监测', to: '/seo/competitors' },
      { id: 'seo-topics', label: '选题采纳', to: '/seo/topics' },
      { id: 'seo-runs', label: '内容运行日志', to: '/seo/runs' },
      { id: 'seo-article-index', label: '文章收录', to: '/seo/article-index' },
      // P7 GEO §3.4：改完 FAQ / 结构化数据之后，回答「AI 会不会引用我们」的那一页
      { id: 'seo-citations', label: 'AI 引用监测', to: '/seo/citations' },
    ],
  },
  {
    id: 'admin',
    label: '管理员',
    icon: ShieldAlert,
    adminOnly: true,
    children: [
      { id: 'admin-home', label: '系统与用户', to: '/admin', exact: true },
      { id: 'admin-changelog', label: '更新日志', to: '/changelog' },
    ],
  },
];

export function getAllModuleChildren(): ModuleChild[] {
  return modules.flatMap((m) => m.children ?? []);
}

// Silence unused import warning — Briefcase reserved for future module/group icons.
void Briefcase;
void Bell;
void Bot;
